import { useEffect, useState } from "react";
import {
    Award,
    Filter,
    Search,
    FileText,
    Mail,
    Phone,
    TrendingUp,
    BarChart3,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { api, fmtEUR, fmtDate, tradeLabel, TRADE_OPTIONS } from "@/lib/api";
import { Link } from "react-router-dom";

export default function MarketStudyPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [tradeFilter, setTradeFilter] = useState("all");

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const r = await api.marketStudy(
                    tradeFilter !== "all" ? { trade: tradeFilter } : {}
                );
                if (alive) setData(r);
            } catch (e) {
                console.error("market study load failed:", e);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [tradeFilter]);

    const filteredItems = (data?.items || []).filter((it) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            (it.contractor_name || "").toLowerCase().includes(q) ||
            (it.project_name || "").toLowerCase().includes(q) ||
            (it.trade || "").toLowerCase().includes(q) ||
            (it.reference || "").toLowerCase().includes(q)
        );
    });

    return (
        <div className="min-h-screen p-8 max-w-[1400px] mx-auto" data-testid="market-study-page">
            <header className="mb-8">
                <div className="text-xs uppercase tracking-[0.18em] text-accent font-semibold">
                    Référence historique · AB Promotion
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl font-extrabold mt-2 flex items-center gap-3">
                    <Award className="w-9 h-9 text-accent" strokeWidth={2} fill="currentColor" />
                    Étude de marché
                </h1>
                <p className="text-sm text-ink-soft mt-2 max-w-2xl">
                    Tous les devis <b>validés définitivement</b> sur l'ensemble
                    de vos chantiers. Utilisez ces prix de référence pour
                    négocier vos futurs marchés et identifier vos partenaires
                    récurrents.
                </p>
            </header>

            {loading ? (
                <div className="p-10 text-center text-sm text-ink-soft">
                    Chargement…
                </div>
            ) : (
                <>
                    {/* Stats banner */}
                    <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="panel p-5">
                            <div className="stat-label">Devis archivés</div>
                            <div className="mt-2 text-3xl font-heading font-extrabold">
                                {data?.total_count || 0}
                            </div>
                        </div>
                        <div className="panel p-5">
                            <div className="stat-label">Montant total signé</div>
                            <div className="mt-2 text-3xl font-heading font-extrabold num">
                                {fmtEUR(data?.total_amount || 0)}
                            </div>
                        </div>
                        <div className="panel p-5">
                            <div className="stat-label">Corps de métier couverts</div>
                            <div className="mt-2 text-3xl font-heading font-extrabold">
                                {data?.trade_stats?.length || 0}
                            </div>
                        </div>
                    </section>

                    {/* Per-trade reference prices */}
                    {(data?.trade_stats || []).length > 0 && (
                        <section className="panel mb-6" data-testid="trade-stats-section">
                            <div className="px-6 py-4 border-b border-border flex items-center gap-2">
                                <BarChart3
                                    className="w-4 h-4 text-accent"
                                    strokeWidth={2}
                                />
                                <h2 className="font-heading font-bold text-lg">
                                    Prix de référence par corps de métier
                                </h2>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-[#FAFAFA] border-b border-border">
                                        <tr>
                                            <th className="px-5 py-3 text-left stat-label">
                                                Métier
                                            </th>
                                            <th className="px-5 py-3 text-right stat-label">
                                                Nb signés
                                            </th>
                                            <th className="px-5 py-3 text-right stat-label">
                                                Min
                                            </th>
                                            <th className="px-5 py-3 text-right stat-label">
                                                Moyenne
                                            </th>
                                            <th className="px-5 py-3 text-right stat-label">
                                                Max
                                            </th>
                                            <th className="px-5 py-3 text-right stat-label">
                                                Cumulé
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.trade_stats.map((s) => (
                                            <tr
                                                key={s.trade}
                                                className="border-b border-border last:border-0 hover:bg-[#FAFAFA]"
                                                data-testid={`trade-stat-${s.trade}`}
                                            >
                                                <td className="px-5 py-3 font-heading font-bold">
                                                    {tradeLabel(s.trade)}
                                                </td>
                                                <td className="px-5 py-3 text-right num">
                                                    {s.count}
                                                </td>
                                                <td className="px-5 py-3 text-right num text-ok">
                                                    {fmtEUR(s.min)}
                                                </td>
                                                <td className="px-5 py-3 text-right num font-bold">
                                                    {fmtEUR(s.avg)}
                                                </td>
                                                <td className="px-5 py-3 text-right num text-danger">
                                                    {fmtEUR(s.max)}
                                                </td>
                                                <td className="px-5 py-3 text-right num">
                                                    {fmtEUR(s.sum)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* Filters */}
                    <section className="panel p-4 mb-6 flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 flex-1 min-w-[280px]">
                            <Search className="w-4 h-4 text-ink-soft" />
                            <Input
                                placeholder="Rechercher entrepreneur, chantier, n° devis…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="rounded-none h-9"
                                data-testid="market-search-input"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-ink-soft" />
                            <Select value={tradeFilter} onValueChange={setTradeFilter}>
                                <SelectTrigger
                                    className="rounded-none h-9 w-[200px]"
                                    data-testid="market-trade-filter"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">
                                        Tous les corps de métier
                                    </SelectItem>
                                    {TRADE_OPTIONS.map((t) => (
                                        <SelectItem key={t.value} value={t.value}>
                                            {t.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </section>

                    {/* Items list */}
                    {filteredItems.length === 0 ? (
                        <div className="panel p-12 text-center">
                            <Award className="w-14 h-14 mx-auto text-ink-soft/30 mb-3" />
                            <div className="font-heading font-bold text-lg mb-1">
                                Aucun devis signé pour le moment
                            </div>
                            <p className="text-sm text-ink-soft max-w-md mx-auto">
                                Cliquez sur l'icône <Award className="inline w-3 h-3" />{" "}
                                sur un devis pour le valider définitivement et
                                l'archiver ici en référence historique.
                            </p>
                        </div>
                    ) : (
                        <section className="panel divide-y divide-border" data-testid="market-items-list">
                            {filteredItems.map((it) => (
                                <MarketItem key={it.id} it={it} />
                            ))}
                        </section>
                    )}
                </>
            )}
        </div>
    );
}

function MarketItem({ it }) {
    return (
        <article
            className="px-6 py-4 grid grid-cols-12 gap-4 items-center hover:bg-[#FAFAFA]"
            data-testid={`market-item-${it.id}`}
        >
            <div className="col-span-4">
                <div className="font-heading font-bold flex items-center gap-2 flex-wrap">
                    <span>{it.contractor_name}</span>
                    <span className="pill border-accent text-white bg-accent font-bold text-[10px]">
                        <Award
                            className="w-3 h-3"
                            strokeWidth={2.5}
                            fill="currentColor"
                        />
                        Signé
                    </span>
                </div>
                <div className="text-xs text-ink-soft mt-1 flex items-center gap-3 flex-wrap">
                    {it.contractor_email && (
                        <a
                            href={`mailto:${it.contractor_email}`}
                            className="flex items-center gap-1 hover:text-accent"
                            data-testid={`market-email-${it.id}`}
                        >
                            <Mail className="w-3 h-3" strokeWidth={1.5} />
                            {it.contractor_email}
                        </a>
                    )}
                    {it.contractor_phone && (
                        <a
                            href={`tel:${it.contractor_phone}`}
                            className="flex items-center gap-1 hover:text-accent"
                        >
                            <Phone className="w-3 h-3" strokeWidth={1.5} />
                            {it.contractor_phone}
                        </a>
                    )}
                    {it.reference && (
                        <span className="text-ink-soft">N° {it.reference}</span>
                    )}
                </div>
                {it.sign_note && (
                    <p className="text-[11px] text-ink-soft mt-1 italic">
                        « {it.sign_note} »
                    </p>
                )}
            </div>
            <div className="col-span-2">
                <span className="pill border-ink/40 text-ink bg-white">
                    {tradeLabel(it.trade)}
                </span>
                <div className="text-[10px] text-ink-soft mt-1 uppercase tracking-wide">
                    {it.items_count} postes
                </div>
            </div>
            <div className="col-span-3">
                <Link
                    to={`/chantiers/${it.project_id}`}
                    className="text-sm font-semibold hover:text-accent"
                    data-testid={`market-project-${it.id}`}
                >
                    {it.project_name}
                </Link>
                {it.project_address && (
                    <div className="text-[11px] text-ink-soft truncate">
                        {it.project_address}
                    </div>
                )}
                <div className="text-[11px] text-ink-soft mt-0.5">
                    Signé le {fmtDate(it.signed_at)}
                </div>
            </div>
            <div className="col-span-2 text-right">
                <div className="num text-lg font-extrabold">
                    {fmtEUR(it.total_ht)}
                </div>
                <div className="text-[11px] text-ink-soft num">
                    TTC {fmtEUR(it.total_ttc)}
                </div>
            </div>
            <div className="col-span-1 flex justify-end">
                {it.source === "pdf" && it.pdf_filename && (
                    <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/devis/${it.id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2 text-ink-soft hover:text-accent"
                        title="Ouvrir le PDF original"
                        data-testid={`market-pdf-${it.id}`}
                    >
                        <FileText className="w-4 h-4" strokeWidth={1.5} />
                    </a>
                )}
            </div>
        </article>
    );
}
